import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { UnsubscribeService } from '@cropx-common/services/shared/UnsubscribeService';
import { ColorContrastHelper } from '@cropx-common/utils/utils';
import { VriMap } from '@cropx-web/services/network/endpoints/FieldApiModels';
import { feature } from '@turf/turf';

import { MapService } from '../../services/map.service';
import { BoundaryLayerComponent } from '../boundary-layer/boundary-layer.component';
import { MapRenderer, baseZIndex } from '../services/map-renderer.service';

@Component({
  selector: 'app-vri-layer',
  imports: [BoundaryLayerComponent, CommonModule],
  template: ` <app-boundary-layer
    [map]="mapService.map$ | async"
    [autoCenter]="false"
    [boundary]="hoverFeature"></app-boundary-layer>`,
  providers: [MapRenderer, UnsubscribeService],
})
export class VriLayerComponent implements OnInit {
  private renderer = inject(MapRenderer);
  private unsub = inject(UnsubscribeService);
  mapService = inject(MapService);

  private zIndex = 100;
  private sectorFeatures: GeoJSON.Feature<GeoJSON.Polygon, { from: number }>[];
  hoverFeature: GeoJSON.Feature<GeoJSON.Polygon>;

  @Input() set map(map: google.maps.Map) {
    this.renderer.setMap(map);
  }

  @Input() set opacity(opacity: number) {
    this.renderer.setOpacity(opacity);
  }

  @Input() set hoverSector(from: number) {
    const hoverFeature = this.sectorFeatures.find(
      (f) => f.properties.from === from,
    );
    if (hoverFeature) {
      this.hoverFeature = feature<GeoJSON.Polygon>(hoverFeature.geometry, {
        from: hoverFeature.properties.from,
        style: {
          fillOpacity: 0,
          strokeColor: '#FFFFFF',
          strokeWeight: 2,
          zIndex: baseZIndex + this.zIndex + 1,
        },
      });
    } else {
      this.hoverFeature = null;
    }
  }

  @Input() set sectors(sectors: VriMap['sectors']) {
    this.hoverFeature = null;
    if (!sectors) {
      this.renderer.setFeatures([]);
    }

    const values = sectors.map((s) => s.applicationDepth);
    const features = sectors.map((sector) => {
      const fillColor = getVriSectorColor(values, sector.applicationDepth);
      const f = feature<GeoJSON.Polygon, { from: number; style: any }>(
        {
          type: 'Polygon',
          coordinates: sector.geometry.coordinates[0],
        },
        {
          from: sector.from,
          style: {
            fillOpacity: 1,
            fillColor: fillColor,
            strokeColor: '#FFFFFF',
            strokeWeight: 0.4,
            zIndex: baseZIndex + this.zIndex,
          },
        },
      );

      return f;
    });

    this.sectorFeatures = features;
    this.renderer.setFeatures(features);
  }

  @Output() hover = new EventEmitter();

  ngOnInit(): void {
    this.renderer.hoverPolygon
      .pipe(this.unsub.takeUntilDestroy)
      .subscribe((e) => {
        const from = e.getProperty('from') ?? null;
        this.hover.emit(from);
      });
  }
}

export const IRRIGATION_GRADIENT = {
  startColor: 'rgb(250, 195, 76)',
  midColor: 'rgb(5, 195, 180)',
  endColor: 'rgb(0, 62, 90)',
};

export const getVriSectorColor = (values: number[], value: number): string => {
  const color = ColorContrastHelper.getScaledColorForRange(
    values,
    value,
    IRRIGATION_GRADIENT.startColor,
    IRRIGATION_GRADIENT.midColor,
    IRRIGATION_GRADIENT.endColor,
    1,
  );

  return color;
};
